import {
  abs,
  cross,
  dot,
  exp,
  Fn,
  float,
  If,
  length,
  max,
  mix,
  mx_noise_float,
  normalize,
  screenUV,
  select,
  smoothstep,
  sqrt,
  uniform,
  vec3,
  vec4,
} from 'three/tsl';
import type { LightRig } from '../lighting/LightRig';
import { noiseWhen } from '../terrain/TerrainStyle';
import type { HollowDef } from '../universe/HollowDef';
import type { Atmosphere } from './Atmosphere';

type N = any;

export interface CloudDef {
  /** Height of the cloud deck above the wall, metres. */
  altitude: number;
  /** Size of the largest cloud features, metres. */
  scale: number;
  /** 0 = clear, 1 = overcast. */
  coverage: number;
  /** Drift speed of the pattern (noise units per second). */
  drift: number;
}

/**
 * A cloud deck on a shell inside the hollow. From the ground you see the nearby
 * deck from below (shadowed bellies, bright rims) and, through its gaps, the far
 * side's clouds from above with their tops lit by the central sun. Because the
 * sun is at the centre, cloud shadows fall straight "down", exactly beneath each
 * cloud, including on the far side overhead.
 */
export class CloudLayer {
  readonly radius: number;
  readonly coverage = uniform(0.5);
  readonly time = uniform(0);

  constructor(
    readonly hollow: HollowDef,
    readonly def: CloudDef,
    readonly rig: LightRig,
  ) {
    this.radius = hollow.radius - def.altitude;
    this.coverage.value = def.coverage;
  }

  update(dt: number): void {
    this.time.value += dt * this.def.drift;
  }

  /**
   * The cloud field for a hollow-local unit direction: x its opacity (0..1), y its thickness
   * (0 at an edge … 1 deep inside). `footprint` is the deck's size of one pixel there (m): noise
   * finer than a few pixels fades out, where it used to break into speckles far away. Without it
   * (shadows on the ground), every octave counts. Drawn decks (`fine`) add two finer octaves,
   * mostly at the edges and in thin cloud: billowing rims and textured bases instead of blurred
   * blobs. In thick cloud they only shade a little (at full strength they punched dark pits).
   */
  fieldAt(dir: N, footprint?: N, fine = false): { alpha: N; thick: N; broad: N; bumps: N } {
    const q = this.deckCoord(dir);
    const k = (f: number) =>
      footprint === undefined ? 1 : smoothstep(1.5, 4, float(this.def.scale / f).div(footprint));
    const octave = (f: number, a: number) => {
      const w = k(f);
      return w === 1 ? mx_noise_float(q.mul(f)).mul(a) : mx_noise_float(q.mul(f)).mul(w).mul(a);
    };
    const broad = octave(1, 0.5).add(octave(2.03, 0.25)).add(octave(4.11, 0.13));
    const o4 = octave(8.3, 0.07);
    let body: N = broad.add(o4).add(this.coverage.sub(0.5).mul(0.8));
    // The finer octaves alone: the heaps on the tops (+) and the gaps between them (−).
    let bumps: N = o4;
    if (fine) {
      // Skipped where they've faded (far away), as a branch: multiplying by 0 saves nothing.
      const detail = (f: number, a: number) => {
        const w = k(f);
        return w === 1 ? mx_noise_float(q.mul(f)).mul(a) : noiseWhen(q.mul(f), w).mul(w).mul(a);
      };
      const edge = float(1)
        .sub(smoothstep(0.12, 0.42, body))
        .mul(0.75)
        .add(0.25);
      const finer = detail(16.9, 0.05).add(detail(34.3, 0.028));
      body = body.add(finer.mul(edge));
      bumps = bumps.add(finer);
    }
    return {
      alpha: smoothstep(0.02, fine ? 0.13 : 0.24, body),
      thick: smoothstep(0.02, 0.4, body),
      broad,
      bumps: bumps.mul(BUMP_SHADE),
    };
  }

  /** Noise coordinates of a hollow-local unit direction on the deck (drifting). */
  private deckCoord(dir: N): N {
    return dir.mul(this.radius / this.def.scale).add(vec3(this.time, this.time.mul(0.37), 0));
  }

  /** The deck's broad shape (its first three octaves, as in fieldAt) at a direction. */
  private broadAt(dir: N): N {
    const q = this.deckCoord(dir);
    return mx_noise_float(q)
      .mul(0.5)
      .add(mx_noise_float(q.mul(2.03)).mul(0.25))
      .add(mx_noise_float(q.mul(4.11)).mul(0.13));
  }

  /**
   * How directly the sun (overhead: it is at the centre) lights a cloud's top at `dir`: the
   * cosine of its slope, reading the broad field as the height of the cloud tops (CLOUD_DEPTH
   * of them). Its slope comes by finite differences along two tangents. Rounded tops, and
   * flanks and the gaps between puffs a little darker: the clouds read as heaps, not blots.
   */
  private sunFacing(dir: N, broad: N): N {
    const helper = select(abs(dir.y).lessThan(0.9), vec3(0, 1, 0), vec3(1, 0, 0));
    const t1 = normalize(cross(dir, helper));
    const t2 = cross(dir, t1);
    const step = (SLOPE_STEP * this.def.scale) / this.radius;
    const d1 = this.broadAt(normalize(dir.add(t1.mul(step)))).sub(broad);
    const d2 = this.broadAt(normalize(dir.add(t2.mul(step)))).sub(broad);
    // Height difference over the step, as a slope (both in metres).
    const k = (CLOUD_DEPTH * this.def.scale) / (SLOPE_STEP * this.def.scale);
    return float(1).div(
      sqrt(
        d1
          .mul(d1)
          .add(d2.mul(d2))
          .mul(k * k)
          .add(1),
      ),
    );
  }

  /** Cloud opacity (0..1) for a hollow-local unit direction. */
  alphaAt(dir: N): N {
    return this.fieldAt(dir).alpha;
  }

  /** Sunlight reaching a hollow-local position through the cloud deck (1 = clear). */
  shadowAt(local: N): N {
    const r = length(local);
    const a = this.alphaAt(local.div(max(r, 1)));
    // Only positions between the deck and the wall are shaded.
    return select(r.greaterThan(this.radius), float(1).sub(a.mul(0.75)), float(1));
  }

  /** Composite both cloud decks over `base` (already atmosphere-applied). */
  apply(base: N, depthNode: N, atmosphere: Atmosphere): N {
    const Rc = this.radius;
    const R = this.hollow.radius;
    const rig = this.rig;
    const sunI = rig.sunIrradiance;
    return Fn(() => {
      const uv = screenUV;
      const ray = atmosphere.viewRay(uv);
      const cam = atmosphere.cameraLocal;
      const depth = depthNode.sample(uv).r;
      const terrainDist = atmosphere.distanceFromDepth(depth, uv);
      const b = dot(cam, ray);
      const c = dot(cam, cam).sub(Rc * Rc);
      const disc = b.mul(b).sub(c);
      const sq = sqrt(max(disc, 0));
      const t1 = b.negate().sub(sq);
      const t2 = b.negate().add(sq);
      const hit = disc.greaterThan(0);

      // How much of the deck a pixel spans, per metre of distance (outside the branches:
      // screen derivatives aren't allowed in them). Assigned: a bare .toVar() of derivatives
      // is emitted where it's first read, here inside the far deck's branch.
      const pixelAngle = float(0).toVar();
      pixelAngle.assign(length(ray.dFdx()).add(length(ray.dFdy())).mul(0.5));
      const shade = (t: N, fromBelow: boolean) => {
        const p = cam.add(ray.mul(t));
        const dir = normalize(p);
        const f = this.fieldAt(dir, max(t.mul(pixelAngle), 1), true);
        const a = f.alpha;
        // Sunlight at the deck (closer to the sun than the wall, so a bit brighter), taken a little
        // above it: on the deck itself the clouds' own shadow (below the deck only) flickered on
        // and off from pixel to pixel, and speckled every cloud.
        const light = rig.lightColorAt(p.mul((Rc - 50) / Rc)).mul(sunI * (R / Rc) ** 2);
        const ambient = vec3(rig.ambientColor as N).mul(rig.veilAt(dir).mul(0.7).add(0.3));
        // From below the thick bellies are dark and the thin edges glow; from above the sunlit
        // tops are brightest where the cloud is thickest, dimmer at its thin, translucent edges.
        const lit = fromBelow
          ? light
              .mul(
                exp(f.thick.add(f.bumps.mul(0.3)).mul(-3))
                  .mul(0.55)
                  .add(0.06),
              )
              .add(ambient.mul(0.5))
          : light
              .mul(this.sunFacing(dir, f.broad).mul(f.thick.mul(0.2).add(0.8)).mul(f.bumps.add(1).max(0.4)))
              .add(ambient.mul(0.3));
        const radiance = lit.mul(0.9 / Math.PI);
        const { S, T } = atmosphere.lookup(uv, t, ray);
        // A flat deck seen edge-on smears into streaks; fade it at grazing angles.
        const grazing = smoothstep(0.08, 0.4, dot(ray, dir).abs());
        return { color: radiance.mul(T).add(S), alpha: a.mul(grazing) };
      };

      // Shade a deck only where the view ray actually crosses it in front of the terrain
      // (real branches: most of the ground near the camera never reaches the deck).
      const out = vec3(base.rgb).toVar();
      const farOn = hit.and(t2.greaterThan(0)).and(t2.lessThan(terrainDist));
      If(farOn, () => {
        const far = shade(t2, false);
        out.assign(mix(out, far.color, far.alpha));
      });
      const nearOn = hit.and(t1.greaterThan(0)).and(t1.lessThan(terrainDist)).and(c.greaterThan(0));
      If(nearOn, () => {
        const near = shade(t1, true);
        out.assign(mix(out, near.color, near.alpha));
      });
      return vec4(out, 1);
    })();
  }
}

/** How much brighter the heaps on the cloud tops are (and darker the gaps), per unit of noise. */
const BUMP_SHADE = 9;
/** Step of the finite differences for the tops' slope, as a share of the deck's scale. */
const SLOPE_STEP = 0.04;
/** How tall the cloud tops are where the broad field rises by 1, as a share of the deck's scale. */
const CLOUD_DEPTH = 0.5;

export const VERDANT_CLOUDS: CloudDef = {
  altitude: 2100,
  scale: 2600,
  // (0.42 before: the far side overhead is the view, so it's kept mostly open.)
  coverage: 0.36,
  drift: 0.004,
};
